/**
 * Вкладка «Уборка/Автозаявки» настроек менеджера (housekeeping wave 2026-06-18).
 *
 *   • Тумблер «Авто-заявка на уборку при выселении» (trigger=stay.checked_out)
 *     — управляет Частью A п.1 (авто-создание заявки выселения).
 *   • Общий список правил «Автозаявки»: добавить/редактировать/удалить.
 *     Форма добавления — ОТДЕЛЬНЫЙ ЭКРАН с «Назад» (AutoTaskRuleForm).
 *
 * RBAC: /v1/request-rules гейтится на бэке (tenant-context, mgmt). Фронт
 * скрывает контролы, когда canManage=false.
 */
import { useMemo, useState } from 'react';
import { Button, Card, Skeleton } from '@hsc/ui';
import type { AutoTaskRule, AutoTaskTrigger } from '@hsc/api-client';
import {
  useAutoTaskRules,
  useDeleteAutoTaskRule,
  useUpdateAutoTaskRule,
} from './useAutoTasks.js';
import { AutoTaskRuleForm } from './AutoTaskRuleForm.js';

const TRIGGER_SHORT: Readonly<Record<AutoTaskTrigger, string>> = {
  'stay.checked_out': 'При выселении',
  'room.cleanliness_stale': 'Чистота устарела',
  'stay.checked_in': 'При заселении',
  recurring_interval: 'Каждые N дней',
  n_days_after_last_completed: 'Через N дней после уборки',
};

export interface AutoTasksPanelProps {
  canManage?: boolean;
}

export function AutoTasksPanel({ canManage = false }: AutoTasksPanelProps): JSX.Element {
  const rulesQ = useAutoTaskRules();
  const update = useUpdateAutoTaskRule();
  const del = useDeleteAutoTaskRule();
  // creating=true → отдельный экран новой формы; editing=rule → правка.
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AutoTaskRule | null>(null);

  const rules = rulesQ.data ?? [];
  const checkoutRule = useMemo(
    () => rules.find((r) => r.trigger === 'stay.checked_out'),
    [rules],
  );

  if (creating) {
    return <AutoTaskRuleForm onBack={() => setCreating(false)} />;
  }
  if (editing) {
    return <AutoTaskRuleForm rule={editing} onBack={() => setEditing(null)} />;
  }

  function toggleCheckout(): void {
    if (!checkoutRule) return;
    update.mutate({
      id: checkoutRule.id,
      input: {
        target_department: checkoutRule.target_department,
        title_template: checkoutRule.title_template,
        priority: checkoutRule.priority,
        enabled: !checkoutRule.enabled,
        scope: checkoutRule.scope,
        ...(checkoutRule.category_code ? { category_code: checkoutRule.category_code } : {}),
        ...(checkoutRule.interval_days ? { interval_days: checkoutRule.interval_days } : {}),
      },
    });
  }

  return (
    <section data-testid="auto-tasks-panel">
      <Card>
        <div className="hsc-toggle-row" data-testid="checkout-toggle-row">
          <div>
            <strong>Авто-заявка на уборку при выселении</strong>
            <p className="ta-muted">
              При выезде гостя номер уходит «На проверке», а в очередь горничных
              автоматически падает заявка «Выселение».
            </p>
          </div>
          {checkoutRule ? (
            <label className="hsc-switch" data-testid="checkout-toggle">
              <input
                type="checkbox"
                checked={checkoutRule.enabled}
                disabled={!canManage || update.isPending}
                onChange={toggleCheckout}
                data-testid="checkout-toggle-input"
              />
              <span className="hsc-switch__slider" />
            </label>
          ) : (
            <span className="ta-muted" data-testid="checkout-toggle-missing">
              Правило не настроено
            </span>
          )}
        </div>
      </Card>

      <div className="fleet-toolbar" data-testid="auto-tasks-toolbar">
        <h2 className="hsc-section__title">Автозаявки</h2>
        {canManage && (
          <Button size="sm" onClick={() => setCreating(true)} data-testid="auto-task-add">
            + Добавить правило
          </Button>
        )}
      </div>

      {rulesQ.isLoading ? (
        <div className="fleet-grid" data-testid="auto-tasks-skeleton">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} height={84} radius={16} />
          ))}
        </div>
      ) : rulesQ.isError ? (
        <Card>
          <p role="alert">Не удалось загрузить правила автозаявок.</p>
        </Card>
      ) : rules.length === 0 ? (
        <Card>
          <p data-testid="auto-tasks-empty">
            Правил пока нет. Добавьте плановую уборку, обновление полотенец или
            другую авто-задачу.
          </p>
        </Card>
      ) : (
        <ul className="fleet-grid" data-testid="auto-tasks-list">
          {rules.map((rule) => (
            <li key={rule.id} className="fleet-card" data-testid={`auto-task-${rule.id}`}>
              <div className="fleet-card__head">
                <span className="fleet-card__title">{rule.title_template}</span>
                <span
                  className={`fleet-badge ${rule.enabled ? 'fleet-badge--ok' : 'fleet-badge--muted'}`}
                >
                  {rule.enabled ? 'Вкл' : 'Выкл'}
                </span>
              </div>
              <div className="fleet-card__meta">
                <span>{TRIGGER_SHORT[rule.trigger]}</span>
                {rule.interval_days ? <span>· каждые {rule.interval_days} дн.</span> : null}
              </div>
              {canManage && (
                <div className="fleet-card__actions">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setEditing(rule)}
                    data-testid={`auto-task-${rule.id}-edit`}
                  >
                    Изменить
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={del.isPending}
                    onClick={() => del.mutate(rule.id)}
                    data-testid={`auto-task-${rule.id}-delete`}
                  >
                    Удалить
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
